import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, Edit, Users, Shield, Building, Users2, Key,
  CheckCircle, XCircle, AlertTriangle, AlertCircle
} from 'lucide-react';
import {
  usuariosService, rolesService, departamentosService, gruposService, modulosService
} from '../../../services/api';

const FeedbackModal = ({ isOpen, type, title, message, onConfirm, onClose, showCancel }) => {
  if (!isOpen) return null;
  const icons = {
    success: <CheckCircle className="text-green-500 w-12 h-12" />,
    error: <XCircle className="text-red-500 w-12 h-12" />,
    warning: <AlertTriangle className="text-yellow-500 w-12 h-12" />,
    info: <AlertCircle className="text-blue-500 w-12 h-12" />
  };
  const colors = {
    success: 'bg-green-600 hover:bg-green-700',
    error: 'bg-red-600 hover:bg-red-700',
    warning: 'bg-yellow-600 hover:bg-yellow-700',
    info: 'bg-blue-600 hover:bg-blue-700'
  };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
      <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6 text-center">
        <div className="flex justify-center mb-4">{icons[type] || icons.info}</div>
        <h3 className="text-lg font-bold text-gray-900 mb-2">{title}</h3>
        <p className="text-sm text-gray-600 mb-6">{message}</p>
        <div className="flex justify-center gap-3">
          {showCancel && (
            <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
              Cancelar
            </button>
          )}
          <button
            onClick={() => { if (onConfirm) onConfirm(); onClose(); }}
            className={`px-4 py-2 text-white rounded-lg ${colors[type] || colors.info}`}
          >
            Aceptar
          </button>
        </div>
      </div>
    </div>
  );
};

const TABS = [
  { id: 'usuarios', label: 'Usuarios', icon: Users },
  { id: 'roles', label: 'Roles', icon: Shield },
  { id: 'departamentos', label: 'Departamentos', icon: Building },
  { id: 'grupos', label: 'Grupos', icon: Users2 },
  { id: 'permisos', label: 'Permisos', icon: Key }
];

const UsuariosView = () => {
  const [tab, setTab] = useState('usuarios');
  const [feedback, setFeedback] = useState({ isOpen: false, type: 'info', title: '', message: '', onConfirm: null, showCancel: false });
  const showFeedback = (type, title, message, onConfirm = null, showCancel = false) => {
    setFeedback({ isOpen: true, type, title, message, onConfirm, showCancel });
  };
  const closeFeedback = () => setFeedback(prev => ({ ...prev, isOpen: false }));

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl lg:text-2xl font-bold text-gray-800">Gestión de Usuarios</h2>
        <p className="text-sm lg:text-base text-gray-600">Usuarios, roles, departamentos, grupos y permisos por función</p>
      </div>

      <div className="flex flex-wrap gap-2 mb-6 border-b border-gray-200">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                tab === t.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon size={16} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'usuarios' && <UsuariosTab showFeedback={showFeedback} />}
      {tab === 'roles' && <CatalogoTab tipo="rol" showFeedback={showFeedback} />}
      {tab === 'departamentos' && <CatalogoTab tipo="departamento" showFeedback={showFeedback} />}
      {tab === 'grupos' && <CatalogoTab tipo="grupo" showFeedback={showFeedback} />}
      {tab === 'permisos' && <PermisosTab showFeedback={showFeedback} />}

      <FeedbackModal {...feedback} onClose={closeFeedback} />
    </div>
  );
};

// ==================== TAB USUARIOS ====================
const UsuariosTab = ({ showFeedback }) => {
  const [usuarios, setUsuarios] = useState([]);
  const [roles, setRoles] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [seleccionado, setSeleccionado] = useState(null);

  const cargar = useCallback(async () => {
    try {
      setLoading(true);
      const [uRes, rRes, dRes] = await Promise.all([
        usuariosService.getAll(), rolesService.getAll(), departamentosService.getAll()
      ]);
      setUsuarios(uRes.data);
      setRoles(rRes.data);
      setDepartamentos(dRes.data);
    } catch (error) {
      showFeedback('error', 'Error', error.message || 'Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  }, [showFeedback]);

  useEffect(() => { cargar(); }, [cargar]);

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button
          onClick={() => { setModalMode('create'); setSeleccionado(null); setShowModal(true); }}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700"
        >
          <Plus size={20} /> Nuevo Usuario
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-x-auto">
        {loading ? (
          <p className="p-8 text-center text-gray-600">Cargando...</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-600">
              <tr>
                <th className="px-4 py-3">Nombre</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Rol</th>
                <th className="px-4 py-3">Departamento</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {usuarios.map(u => (
                <tr key={u.id}>
                  <td className="px-4 py-3 font-medium text-gray-800">{u.nombre}</td>
                  <td className="px-4 py-3 text-gray-600">{u.email}</td>
                  <td className="px-4 py-3 text-gray-600">{u.rol}</td>
                  <td className="px-4 py-3 text-gray-600">{u.departamento || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${u.activo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {u.activo ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => { setModalMode('edit'); setSeleccionado(u); setShowModal(true); }}
                      className="text-green-600 hover:text-green-800 p-2 rounded-lg hover:bg-green-100"
                      title="Editar"
                    >
                      <Edit size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <UsuarioModal
          mode={modalMode}
          usuario={seleccionado}
          roles={roles}
          departamentos={departamentos}
          onClose={() => setShowModal(false)}
          onSuccess={(mensaje) => { setShowModal(false); cargar(); if (mensaje) showFeedback('success', 'Éxito', mensaje); }}
        />
      )}
    </div>
  );
};

const UsuarioModal = ({ mode, usuario, roles, departamentos, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    nombre: '', email: '', password: '', rol: 'usuario', departamento: '', telefono: '', activo: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (usuario) {
      setFormData({
        nombre: usuario.nombre, email: usuario.email, password: '',
        rol: usuario.rol, departamento: usuario.departamento || '',
        telefono: usuario.telefono || '', activo: usuario.activo !== undefined ? usuario.activo : true
      });
    }
  }, [usuario]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const payload = { ...formData, departamento: formData.departamento || null };
      if (mode === 'create') {
        await usuariosService.create(payload);
        onSuccess('Usuario creado exitosamente');
      } else {
        await usuariosService.update(usuario.id, payload);
        onSuccess('Usuario actualizado exitosamente');
      }
    } catch (err) {
      setError(err.message || 'Error al guardar usuario');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-xl font-bold text-gray-800">{mode === 'create' ? 'Nuevo Usuario' : 'Editar Usuario'}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700"><span className="text-2xl">×</span></button>
        </div>
        <div className="p-6 space-y-4">
          {error && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">{error}</div>}
          <div>
            <label className="block text-gray-700 font-medium mb-2">Nombre <span className="text-red-500">*</span></label>
            <input type="text" name="nombre" value={formData.nombre} onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
          </div>
          <div>
            <label className="block text-gray-700 font-medium mb-2">Email <span className="text-red-500">*</span></label>
            <input type="email" name="email" value={formData.email} onChange={handleChange}
              disabled={mode === 'edit'}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg disabled:bg-gray-100" />
          </div>
          {mode === 'create' && (
            <div>
              <label className="block text-gray-700 font-medium mb-2">Contraseña <span className="text-red-500">*</span></label>
              <input type="password" name="password" value={formData.password} onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-700 font-medium mb-2">Rol</label>
              <select name="rol" value={formData.rol} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg">
                {roles.map(r => <option key={r.codigo} value={r.codigo}>{r.nombre}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-2">Departamento</label>
              <select name="departamento" value={formData.departamento} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg">
                <option value="">— Sin asignar —</option>
                {departamentos.map(d => <option key={d.codigo} value={d.codigo}>{d.nombre}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-gray-700 font-medium mb-2">Teléfono</label>
            <input type="text" name="telefono" value={formData.telefono} onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
          </div>
          {mode === 'edit' && (
            <div className="flex items-center">
              <input type="checkbox" name="activo" checked={formData.activo} onChange={handleChange}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded" />
              <label className="ml-2 text-gray-700 font-medium">Usuario activo</label>
            </div>
          )}
        </div>
        <div className="p-6 border-t border-gray-200 flex justify-end gap-4">
          <button onClick={onClose} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancelar</button>
          <button onClick={handleSubmit} disabled={loading}
            className={`px-6 py-2 rounded-lg text-white ${loading ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'}`}>
            {loading ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ==================== TAB ROLES / DEPARTAMENTOS / GRUPOS ====================
// Los 3 catalogos comparten shape (codigo|id, nombre, activo) salvo grupos
// que usa `id` numerico + `descripcion` en vez de `codigo` de texto -- un
// solo componente parametrizado por `tipo` evita triplicar el mismo CRUD.
const CONFIG_TIPO = {
  rol: { service: rolesService, campoId: 'codigo', titulo: 'Rol', pideCodigo: true },
  departamento: { service: departamentosService, campoId: 'codigo', titulo: 'Departamento', pideCodigo: true },
  grupo: { service: gruposService, campoId: 'id', titulo: 'Grupo', pideCodigo: false }
};

const CatalogoTab = ({ tipo, showFeedback }) => {
  const cfg = CONFIG_TIPO[tipo];
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [seleccionado, setSeleccionado] = useState(null);

  const cargar = useCallback(async () => {
    try {
      setLoading(true);
      const res = await cfg.service.getAll();
      setItems(res.data);
    } catch (error) {
      showFeedback('error', 'Error', error.message || `Error al cargar ${cfg.titulo.toLowerCase()}s`);
    } finally {
      setLoading(false);
    }
  }, [cfg, showFeedback]);

  useEffect(() => { cargar(); }, [cargar]);

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button
          onClick={() => { setModalMode('create'); setSeleccionado(null); setShowModal(true); }}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700"
        >
          <Plus size={20} /> Nuevo {cfg.titulo}
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-x-auto">
        {loading ? (
          <p className="p-8 text-center text-gray-600">Cargando...</p>
        ) : items.length === 0 ? (
          <p className="p-8 text-center text-gray-600">No hay {cfg.titulo.toLowerCase()}s registrados</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-600">
              <tr>
                {cfg.pideCodigo && <th className="px-4 py-3">Código</th>}
                <th className="px-4 py-3">Nombre</th>
                {tipo === 'grupo' && <th className="px-4 py-3">Descripción</th>}
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map(item => (
                <tr key={item[cfg.campoId]}>
                  {cfg.pideCodigo && <td className="px-4 py-3 text-gray-600">{item.codigo}</td>}
                  <td className="px-4 py-3 font-medium text-gray-800">{item.nombre}</td>
                  {tipo === 'grupo' && <td className="px-4 py-3 text-gray-600">{item.descripcion || '—'}</td>}
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${item.activo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {item.activo ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => { setModalMode('edit'); setSeleccionado(item); setShowModal(true); }}
                      className="text-green-600 hover:text-green-800 p-2 rounded-lg hover:bg-green-100"
                      title="Editar"
                    >
                      <Edit size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <CatalogoModal
          tipo={tipo}
          cfg={cfg}
          mode={modalMode}
          item={seleccionado}
          onClose={() => setShowModal(false)}
          onSuccess={(mensaje) => { setShowModal(false); cargar(); if (mensaje) showFeedback('success', 'Éxito', mensaje); }}
        />
      )}
    </div>
  );
};

const CatalogoModal = ({ tipo, cfg, mode, item, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({ codigo: '', nombre: '', descripcion: '', activo: true });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (item) {
      setFormData({
        codigo: item.codigo || '', nombre: item.nombre || '',
        descripcion: item.descripcion || '', activo: item.activo !== undefined ? item.activo : true
      });
    }
  }, [item]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      if (mode === 'create') {
        const payload = cfg.pideCodigo
          ? { codigo: formData.codigo, nombre: formData.nombre }
          : { nombre: formData.nombre, descripcion: formData.descripcion };
        await cfg.service.create(payload);
        onSuccess(`${cfg.titulo} creado exitosamente`);
      } else {
        const id = item[cfg.campoId];
        const payload = tipo === 'grupo'
          ? { nombre: formData.nombre, descripcion: formData.descripcion, activo: formData.activo }
          : { nombre: formData.nombre, activo: formData.activo };
        await cfg.service.update(id, payload);
        onSuccess(`${cfg.titulo} actualizado exitosamente`);
      }
    } catch (err) {
      setError(err.message || `Error al guardar ${cfg.titulo.toLowerCase()}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-xl font-bold text-gray-800">{mode === 'create' ? `Nuevo ${cfg.titulo}` : `Editar ${cfg.titulo}`}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700"><span className="text-2xl">×</span></button>
        </div>
        <div className="p-6 space-y-4">
          {error && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">{error}</div>}
          {cfg.pideCodigo && mode === 'create' && (
            <div>
              <label className="block text-gray-700 font-medium mb-2">Código <span className="text-red-500">*</span></label>
              <input type="text" name="codigo" value={formData.codigo} onChange={handleChange}
                placeholder="ej: ventas, soporte"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
            </div>
          )}
          <div>
            <label className="block text-gray-700 font-medium mb-2">Nombre <span className="text-red-500">*</span></label>
            <input type="text" name="nombre" value={formData.nombre} onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
          </div>
          {tipo === 'grupo' && (
            <div>
              <label className="block text-gray-700 font-medium mb-2">Descripción</label>
              <textarea name="descripcion" value={formData.descripcion} onChange={handleChange} rows="2"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
            </div>
          )}
          {mode === 'edit' && (
            <div className="flex items-center">
              <input type="checkbox" name="activo" checked={formData.activo} onChange={handleChange}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded" />
              <label className="ml-2 text-gray-700 font-medium">Activo</label>
            </div>
          )}
        </div>
        <div className="p-6 border-t border-gray-200 flex justify-end gap-4">
          <button onClick={onClose} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancelar</button>
          <button onClick={handleSubmit} disabled={loading}
            className={`px-6 py-2 rounded-lg text-white ${loading ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'}`}>
            {loading ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ==================== TAB PERMISOS (matriz estilo Django admin) ====================
const DESTINO_SERVICE = {
  usuario: { service: usuariosService, campoId: 'id', campoLabel: 'nombre' },
  rol: { service: rolesService, campoId: 'codigo', campoLabel: 'nombre' },
  departamento: { service: departamentosService, campoId: 'codigo', campoLabel: 'nombre' },
  grupo: { service: gruposService, campoId: 'id', campoLabel: 'nombre' }
};

const PermisosTab = ({ showFeedback }) => {
  const [tipoDestino, setTipoDestino] = useState('usuario');
  const [opciones, setOpciones] = useState([]);
  const [destinoId, setDestinoId] = useState('');
  const [modulos, setModulos] = useState([]);
  const [matriz, setMatriz] = useState({});
  const [loading, setLoading] = useState(false);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    modulosService.getAll().then(res => setModulos(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setDestinoId('');
    setMatriz({});
    setOpciones([]); // evita renderizar opciones del tipo anterior con el campoId del nuevo tipo
    DESTINO_SERVICE[tipoDestino].service.getAll()
      .then(res => setOpciones(res.data))
      .catch(() => setOpciones([]));
  }, [tipoDestino]);

  useEffect(() => {
    if (!destinoId || modulos.length === 0) { setMatriz({}); return; }
    setLoading(true);
    DESTINO_SERVICE[tipoDestino].service.getPermisos(destinoId)
      .then(res => {
        const mapa = {};
        for (const m of modulos) {
          const fila = res.data.find(p => p.modulo === m.codigo);
          mapa[m.codigo] = fila
            ? { puede_ver: fila.puede_ver, puede_crear: fila.puede_crear, puede_editar: fila.puede_editar, puede_borrar: fila.puede_borrar }
            : { puede_ver: false, puede_crear: false, puede_editar: false, puede_borrar: false };
        }
        setMatriz(mapa);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [destinoId, tipoDestino, modulos]);

  const toggle = (moduloCodigo, campo) => {
    setMatriz(prev => ({
      ...prev,
      [moduloCodigo]: { ...prev[moduloCodigo], [campo]: !prev[moduloCodigo][campo] }
    }));
  };

  const guardar = async () => {
    setGuardando(true);
    try {
      const filas = Object.entries(matriz).map(([modulo, flags]) => ({ modulo, ...flags }));
      await DESTINO_SERVICE[tipoDestino].service.setPermisos(destinoId, filas);
      showFeedback('success', 'Éxito', 'Permisos guardados exitosamente');
    } catch (error) {
      showFeedback('error', 'Error', error.message || 'Error al guardar permisos');
    } finally {
      setGuardando(false);
    }
  };

  const gruposPorCategoria = modulos.reduce((acc, m) => {
    (acc[m.grupo] = acc[m.grupo] || []).push(m);
    return acc;
  }, {});

  return (
    <div>
      <div className="bg-white rounded-lg shadow p-4 mb-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-gray-700 font-medium mb-2">Asignar permisos a</label>
          <select value={tipoDestino} onChange={(e) => setTipoDestino(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg">
            <option value="usuario">Usuario</option>
            <option value="rol">Rol</option>
            <option value="departamento">Departamento</option>
            <option value="grupo">Grupo</option>
          </select>
        </div>
        <div>
          <label className="block text-gray-700 font-medium mb-2">{tipoDestino.charAt(0).toUpperCase() + tipoDestino.slice(1)}</label>
          <select value={destinoId} onChange={(e) => setDestinoId(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg">
            <option value="">— Selecciona —</option>
            {opciones.map(o => (
              // key usa id ?? codigo (no el campoId del tipo actual): en el
              // frame de transicion entre tipos, `opciones` aun trae datos
              // del tipo anterior antes de que el efecto lo limpie, y
              // o[campoId-nuevo] daria undefined para todas -- esto evita
              // la colision de keys en ese frame intermedio.
              <option key={o.id ?? o.codigo} value={o[DESTINO_SERVICE[tipoDestino].campoId]}>
                {o[DESTINO_SERVICE[tipoDestino].campoLabel]}
              </option>
            ))}
          </select>
        </div>
      </div>

      {!destinoId ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          Selecciona un destino para ver y editar su matriz de permisos.
        </div>
      ) : loading ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-600">Cargando permisos...</div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-600">
              <tr>
                <th className="px-4 py-3">Módulo</th>
                <th className="px-4 py-3 text-center">Ver</th>
                <th className="px-4 py-3 text-center">Crear</th>
                <th className="px-4 py-3 text-center">Editar</th>
                <th className="px-4 py-3 text-center">Borrar</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {Object.entries(gruposPorCategoria).map(([grupo, mods]) => (
                <React.Fragment key={grupo}>
                  <tr className="bg-gray-50">
                    <td colSpan={5} className="px-4 py-1 text-xs font-semibold text-gray-500 uppercase">{grupo}</td>
                  </tr>
                  {mods.map(m => (
                    <tr key={m.codigo}>
                      <td className="px-4 py-3 font-medium text-gray-800">{m.nombre}</td>
                      {['puede_ver', 'puede_crear', 'puede_editar', 'puede_borrar'].map(campo => (
                        <td key={campo} className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={matriz[m.codigo]?.[campo] || false}
                            onChange={() => toggle(m.codigo, campo)}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded"
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          <div className="p-4 border-t border-gray-200 flex justify-end">
            <button onClick={guardar} disabled={guardando}
              className={`px-6 py-2 rounded-lg text-white ${guardando ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'}`}>
              {guardando ? 'Guardando...' : 'Guardar permisos'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsuariosView;
